<script setup lang="ts">
import type { OrderLineFragment, SearchProductVariantResultFragment, SearchResultFragment } from '#graphql-operations'
import type { MaybeComputedElementRef } from '@vueuse/core'
import { COLOR_PRODUCT_OPTION_GROUP } from '@layers/common/app/constants'
import * as Sentry from '@sentry/nuxt'

const { ecommerce } = useAnalytics()
const { t } = useI18n()
const priceFormatter = usePriceFormatter()
const drawers = useDrawers()
const route = useRoute()
const { toast } = useToast()

const total = ref(0)
const isLoading = ref(false)
const isMounted = ref(false)
const vatModalOpen = ref(false)
const showBottomSheet = ref(false)
const bottomSheetScrollContainer = ref<HTMLElement | null>(null)
const { arrivedState } = useScroll(bottomSheetScrollContainer)

const activeOrderStore = useActiveOrderStore()
const rating = useRating()

const productStore = useProductStore()
const productId = computed(() => route.params.id as string)

if (import.meta.client) {
  onBeforeMount(() => {
    productStore.fetchProduct(productId.value).then(productStore.initializeFromQuery)
  })
}

onBeforeUnmount(productStore.reset)

const reviewRating = computed(() => {
  if (!productStore.product?.customProductMappings.reviewRating)
    return 0
  return Math.round(productStore.product.customProductMappings.reviewRating * 100) / 100
})

// Find the image index of the current variant (full selection or just color selection)
const selectedVariantImageIndex = computed(() => {
  // If all options are selected, use the selected variant's image
  if (productStore.selectedVariant?.featuredAsset?.id) {
    return productStore.images?.findIndex(image => image.id === productStore.selectedVariant?.featuredAsset?.id) ?? 0
  }

  // If only color is selected, find a variant with that color
  const colorOption = productStore.selectedOptions[COLOR_PRODUCT_OPTION_GROUP]
  if (colorOption) {
    const colorVariant = productStore.productVariants?.find(v =>
      v.options?.some(option => option.group.code === COLOR_PRODUCT_OPTION_GROUP && option.code === colorOption),
    )
    if (colorVariant?.featuredAsset?.id) {
      return productStore.images?.findIndex(image => image.id === colorVariant.featuredAsset?.id) ?? 0
    }
  }

  return 0
})

const currentImageIndex = ref(selectedVariantImageIndex.value)

const addingToCart = ref(false)
const addToCartButtonRef: MaybeComputedElementRef = ref(null)
const addToCartButtonVisible = useElementVisibility(addToCartButtonRef)
const showOptionErrors = ref(false)

async function handleAddToCart(selectedVariant?: SearchProductVariantResultFragment, quantity = 1) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  if (!productStore.allOptionsSelected) {
    showOptionErrors.value = true
    Sentry.captureMessage('Not all product options selected', {
      extra: {
        productId: productStore.product?.id,
        productVariantId: selectedVariant?.id,
      },
    })
    toast({
      title: t('product.error.please_select_all_options'),
      variant: 'destructive',
    })
    return
  }

  if (!selectedVariant) {
    Sentry.captureMessage('No product variant selected', {
      extra: { productId: productStore.product?.id },
    })
    toast({
      title: t('product.error.variant_not_found'),
      variant: 'destructive',
    })
    return
  }

  addingToCart.value = true

  try {
    await activeOrderStore.addItemToOrder(selectedVariant, quantity)
    ecommerce.addToCart(selectedVariant, quantity)
    showBottomSheet.value = false
    drawers.value.cart.open = true
    ecommerce.viewCart()
  }
  catch (error) {
    Sentry.captureException(error)
    if (error instanceof Error)
      toast({ title: error.message, variant: 'destructive' })
  }
  finally {
    addingToCart.value = false
  }
}

async function handleAdjustOrderLine(orderLine: OrderLineFragment, quantity: number) {
  if (productStore.status === 'pending' || addingToCart.value)
    return

  addingToCart.value = true
  await activeOrderStore.adjustOrderLine(orderLine, quantity)
  addingToCart.value = false

  if (quantity > orderLine.quantity) {
    ecommerce.addToCart(orderLine, quantity)
    drawers.value.cart.open = true
    ecommerce.viewCart()
  }
  else {
    ecommerce.removeFromCart(orderLine, quantity)
  }
}

const { currentlyViewingCount } = useProductMetrics(productId.value)

const productHistoryStore = useProductHistoryStore()
const historyItems = computed(() => productHistoryStore?.history.filter(item => item.id !== productId.value))

function handleClick(item: SearchResultFragment) {
  productHistoryStore.addItemToHistory(item)
  ecommerce.selectItem(item)
}

const title = computed(() => productStore.product?.name ?? t('pages.title.index'))
const description = computed(() => stripHTMLTags(productStore.product?.description ?? ''))
const image = computed(() => productStore.product?.featuredAsset?.preview)

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: image,
})

watch(() => productStore.status, (currentStatus, oldStatus) => {
  if (oldStatus === 'pending' && currentStatus === 'success' && !productStore.product) {
    showError({
      status: 404,
      statusText: t('error.product_not_found'),
    })
  }
})

const sizeChartModalOpen = ref(false)
const hasSizeChart = computed(() => productStore.product?.description?.includes('<table'))
const tables = computed(() => {
  if (typeof document !== 'undefined' && hasSizeChart.value && productStore.product?.description)
    return extractTablesFromHTML(productStore.product.description)
  return []
})
const showLightbox = ref(false)
const lightboxKey = ref(Date.now())
function openLightbox(index: number) {
  lightboxKey.value = Date.now()
  currentImageIndex.value = index
  showLightbox.value = true
}

const productRating = computed(() => {
  if (!productStore.product?.id)
    return 0
  return rating.getRating(productStore.product.id, 1, [4])
})

onMounted(async () => {
  isMounted.value = true
  window.scrollTo(0, 0)
})

const productLoading = computed(() => productStore.status === 'pending')
</script>

<template>
  <div>
    <div v-if="productStore.status === 'pending'">
      Loading...
    </div>

    <div v-else-if="productStore.status === 'error'">
      {{ t('error.404') }}
    </div>

    <div v-else>
      <div v-if="productStore.product?.id && !addToCartButtonVisible && isMounted" class="pb-safe sm:hidden">
        <div class="fixed bottom-0 left-0 right-0 z50 border-t n-border-base bg-white p4 pb6 shadow">
          <AddToCartButton
            :loading="addingToCart"
            :skeleton="addingToCart"
            @click="showBottomSheet = true"
          />
        </div>
      </div>

      <main class="mx-auto max-w-2xl pb8 lg:max-w-83rem">
        <!-- Product -->
        <div class="mb6">
          <div class="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-10 sm:pt8">
            <!-- Product image -->
            <div class="lg:col-span-4 lg:row-end-1">
              <NSkeleton v-if="!productStore.images" shape="rect" class="aspect-1/1 w-full" />
              <ProductGallery
                v-else
                :images="productStore.images"
                :image-index="selectedVariantImageIndex"
                @update:image-index="currentImageIndex = $event"
              >
                <div class="absolute left-2 top-2 z1">
                  <div class="flex flex-row items-start space-x-2">
                    <NTip
                      v-if="productStore.discountPercentage > 0" n="xs"
                      icon="i-hugeicons:discount-tag-02"
                      class="bg-main-800/80 text-main-200 font-bold backdrop-blur"
                    >
                      {{ t('general.discount', [`${productStore.discountPercentage} %`]) }}
                    </NTip>

                    <NTip
                      v-if="productStore.discountPercentage > 0"
                      n="xs" icon="i-hugeicons:money-add-02"
                      class="bg-main-300/80 text-main-950 font-bold backdrop-blur"
                    >
                      {{ t('general.you_save', [priceFormatter(productStore.amountSaved, productStore.product?.currency)]) }}
                    </NTip>
                  </div>
                </div>
                <div
                  class="absolute start-0 bottom-0 z2 flex flex-col items-start px4 pb3.75 pt4 transition-all duration-300 ease"
                >
                  <div class="group inline-flex align-middle">
                    <button
                      class="h12 min-w-12 inline-flex flex-nowrap cursor-pointer items-center justify-center of-hidden rounded-full bg-white text-sm !p0 hover:c-slate-500"
                      @click="openLightbox(currentImageIndex)"
                    >
                      <span class="h12 w12 inline-flex items-center justify-center">
                        <Icon name="i-heroicons:arrows-pointing-out" class="text-lg" />
                      </span>
                      <span
                        class="max-w-0 of-hidden whitespace-nowrap p0 fw600 transition-max-width,padding duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.15)] group-hover:max-w-[280px] group-hover:pe-[25px]"
                      >
                        {{ t('gallery.click_to_enlarge') }}
                      </span>
                    </button>
                  </div>
                </div>
              </ProductGallery>

              <ProductGalleryLightbox
                v-if="productStore.images"
                :key="lightboxKey"
                v-model:show="showLightbox"
                :images="productStore.images"
                :image-index="currentImageIndex"
              />
            </div>

            <!-- Product details -->
            <div class="mx-auto mt6 max-w-2xl w-full lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt0 lg:max-w-none sm:mt6">
              <div class="px4 lg:px8 sm:px6">
                <ProductBreadcrumbs v-if="productStore.productBreadcrumbs?.length" :items="productStore.productBreadcrumbs" class="mb1 hidden md:block" />

                <!-- Title -->
                <NSkeleton v-if="!productStore.product?.name" />
                <h1 v-else class="text-base fw600" data-testid="productName" v-text="productStore.product.name" />

                <div class="mt2 flex items-center">
                  <ContentGuard>
                    <h2 class="sr-only" v-text="t('general.reviews.label')" />
                    <div class="flex items-center">
                      <Rating :rating="reviewRating || productRating" class="-ml0.5" />

                      <NSkeleton v-if="productLoading" class="ml2 w3" />
                      <span v-else class="ml1 mt1 block text-sm text-slate4 fw600" v-text="reviewRating || productRating" />
                    </div>
                  </ContentGuard>
                </div>

                <!-- Price -->
                <div class="mt1">
                  <NSkeleton v-if="!productStore?.price" class="w-1/4" n="2xl" />
                  <template v-else>
                    <div class="flex items-center justify-start space-x-2">
                      <span class="text-2xl text-red-600 font-bold">
                        {{ priceFormatter(productStore?.price, productStore.product?.currency) }}
                      </span>
                      <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore?.price">
                        <span class="text-xl text-slate5 line-through">
                          {{ priceFormatter(productStore.priceBeforeDiscount, productStore.product?.currency) }}
                        </span>
                        <NBadge v-if="productStore.discountPercentage" n="xs" class="text-main-950 bg-main-200 border-main-950 fw600 tracking-wider !rd-full">
                          -{{ productStore.discountPercentage }}%
                        </NBadge>
                      </template>
                    </div>

                    <div v-if="productStore.amountSaved">
                      <p class="text-sm text-gray-6">
                        {{ t('general.actual_discount') }}
                        <span class="text-red-600 font-semibold">
                          {{ priceFormatter(productStore.amountSaved, productStore.product?.currency) }}
                        </span>
                      </p>
                    </div>
                  </template>
                </div>

                <NSkeleton v-if="productLoading" />
                <div v-else class="mt2 flex flex-col">
                  <span class="text-context text-green6 fw600">
                    {{ t('product.in_stock') }}
                  </span>
                  <a href="#productInformation" class="py-0.5 text-sm text-gray-7 underline">
                    {{ t('product.more_information') }}
                  </a>
                </div>

                <ContentGuard>
                  <section class="mb6 mt4">
                    <template v-if="productLoading">
                      <div v-for="n in 4" :key="`feature-${n}`" class="mb2 flex">
                        <NSkeleton shape="rect" class="mr2 w6 rounded-full" />
                        <NSkeleton :class="n === 2 ? 'w3/4' : 'w1/2'" />
                      </div>
                    </template>

                    <template v-else>
                      <div class="flex flex-col space-y-2">
                        <ProductFeature
                          text-class="text-slate-900 fw600"
                          icon-class="text-slate-950"
                          icon="i-hugeicons:delivery-truck-01" :text="t('trustable.productpage.express_shipping')"
                        />
                        <ProductFeature
                          text-class="text-slate-900 fw600"
                          icon-class="text-slate-950"
                          icon="i-hugeicons:gift" :text="t('promotion.purchase_competition_prizes', ['4 000 000 Kč'])"
                        />
                        <ProductFeature
                          text-class="text-slate-900 fw600"
                          icon-class="text-slate-950"
                          icon="i-hugeicons:truck-return"
                          :text="t('trustable.productpage.return_xx_days', [$config.public.returnWindowDays])"
                        />
                      </div>
                    </template>
                  </section>
                </ContentGuard>

                <NModal :show="sizeChartModalOpen" @close="sizeChartModalOpen = false">
                  <HDialogPanel
                    class="max-w-3xl w-full transform overflow-hidden rounded-md bg-white p3 text-left align-middle shadow-xl transition-all md:p6"
                  >
                    <div class="absolute right-0 top-0 pr4 pt4">
                      <NButton
                        id="close-size-table"
                        n="lg borderless"
                        icon="i-heroicons:x-mark"
                        class="p-0.25em text-context children:m-0"
                        @click="sizeChartModalOpen = false"
                      />
                    </div>

                    <div class="text-center">
                      <h3 id="modal-title" class="text-lg c-slate9 font-semibold leading-6">
                        {{ t('product.size_chart.title') }}
                      </h3>

                      <div class="py4">
                        <div
                          v-for="(table, index) in tables" :key="index"
                          class="mt4 of-x-auto border n-border-200 rounded-lg border-solid bg-background"
                        >
                          <ProductSizeChart :table="table" />
                        </div>
                      </div>

                      <div>
                        {{ t('product.size_chart.note') }}
                      </div>
                    </div>
                  </HDialogPanel>
                </NModal>

                <!-- Variant -->
                <div class="mb4" data-testid="productForm">
                  <div class="mb4 pb2">
                    <ProductOptionSelector
                      v-model:show-option-errors="showOptionErrors"
                      v-model:size-chart-modal-open="sizeChartModalOpen"
                      :has-size-chart
                    />
                  </div>
                  <NSkeleton v-if="productLoading" class="mb2 mt2 h-10 w-1/2" />
                  <!-- Add to cart -->
                  <div>
                    <AddToCart
                      ref="addToCartButtonRef"
                      class="pt2"
                      :loading="addingToCart"
                      @add-to-cart="handleAddToCart(productStore.activeVariant)"
                      @change:order-line="handleAdjustOrderLine"
                    />
                  </div>
                  <NSkeleton v-if="productLoading" class="mt2 w-full" pusle />
                  <div
                    v-else
                    class="text align-center text-primary-950 my2 flex items-center justify-center rounded-md bg-yellow-100 py1 text-xs"
                  >
                    <Icon name="i-hugeicons:shipping-truck-01" class="text-primary-950 mr1" />
                    <div class="mt0.2">
                      {{ t('policies.shipping_from_zero') }}
                    </div>
                  </div>
                </div>

                <!-- Guarantees & trustabbles -->
                <ContentGuard>
                  <div class="py1">
                    <NSkeleton v-if="productLoading" class="h-40 w-full" />
                    <NuxtImg v-else src="/webcontent/guarantee.svg" />
                  </div>
                </ContentGuard>
                <ContentGuard>
                  <section class="mb6 mt4">
                    <template v-if="productLoading">
                      <div v-for="n in 4" :key="`feature-${n}`" class="mb2 flex">
                        <NSkeleton shape="rect" class="mr2 w6 rounded-full" />
                        <NSkeleton :class="n === 2 ? 'w3/4' : 'w1/2'" />
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex flex-col space-y-2">
                        <ProductFeature
                          n="xl"
                          wrapper-class="border-b-1 border-slate-200 py1 pb2"
                          text-class="text-slate-900 pt0.5"
                          icon-class="text-xl animate-pulse text-red-600"
                          icon="i-hugeicons:alert-diamond"
                          :text="`${t('trustable.productpage.currently_viewing')} ${currentlyViewingCount}`"
                        />
                        <ProductFeature
                          wrapper-class="border-b-1 border-slate-200 py1 pb2"
                          text-class="text-slate-900 pt0.5"
                          icon-class="text-slate-950 text-xl"
                          icon="i-hugeicons:shield-energy"
                          :text="t('trustable.productpage.guarantee_satisfaction_program')"
                        />
                        <ProductFeature
                          wrapper-class="border-b-1 border-slate-200 py1 pb2"
                          text-class="text-slate-900 pt0.5"
                          icon-class="text-slate-950 text-xl"
                          icon="i-hugeicons:gift" :text="t('trustable.productpage.gift_to_every_paid_order')"
                        />
                        <ProductFeature
                          wrapper-class="py1"
                          text-class="text-slate-900 pt0.5"
                          icon-class="text-slate-950 text-xl"
                          icon="i-hugeicons:circle-arrow-move-down-left"
                          :text="t('trustable.productpage.change_xx_days', [$config.public.returnWindowDays])"
                        />
                      </div>
                    </template>
                  </section>
                </ContentGuard>
              </div>

              <div class="mb4 block bg-slate/30 pb4 shadow-inner sm:hidden" />

              <!-- Payment acceptance -->
              <div v-if="!productLoading" class="px4 lg:px8 sm:px6">
                <div class="flex items-center text-context" n="slate5">
                  <p class="text-sm fw600" v-text="t('payment_acceptance.payment_method')" />
                </div>
                <div>
                  <PaymentLogos />
                </div>
              </div>

              <!-- Customer protection -->
              <CustomerProtection />
              <Competetion />
            </div>
          </div>
        </div>
        <!-- Verified by customers -->
        <section class="my-4 px-4 md:px0">
          <div class="flex flex-col items-center justify-center border-1 border-gray-3 rounded-md py6">
            <div class="flex items-center justify-center gap-x-1">
              <Icon name="i-heroicons:check-circle-16-solid" class="text-xl text-green-600" />
              <span class="text-md text-gray-8">{{ t('trustable.productpage.verified_by_customers') }}</span>
            </div>
            <p class="text-sm text-gray-6">
              {{ t('trustable.productpage.product_get_good_reviews') }}
            </p>
          </div>
        </section>
        <!-- Product description -->

        <section class="md:align-center m-y-4 rounded-md bg-gray-50 p-x-4 p-y-2 md:m-y-8 md:flex md:justify-center md:p-6 md:py-8">
          <!-- Description -->
          <div id="description" class="block md:basis-1/2">
            <template v-if="!productStore.product?.description">
              <NSkeleton class="min-h-10 w-1/2" />
              <NSkeleton class="w-3/4" />
              <NSkeleton class="w-3/4" />
              <NSkeleton class="w-3/4" />
              <NSkeleton class="w-3/5" />
              <NSkeleton class="w-2/3" />
              <NSkeleton class="w-3/4" />
              <NSkeleton class="w-1/4" />
            </template>
            <div
              v-else
              class="p-4 [&_ul]:mx-0 [&_ul]:my-1.5 [&_ul]:list-none [&_p]:mb-1.5 [&_ul_li]:ml-4 [&_table]:w-full [&_table]:table-auto [&_table]:overflow-x-auto [&_table]:border [&_table]:border-gray-300 [&_table]:rounded-md [&_table]:bg-white [&_table]:text-sm [&_li]:list-disc [&_td]:border-t [&_td]:border-gray-200 [&_table_tr:first-child_td:first-child]:rounded-tl-md [&_table_tr:first-child_td:last-child]:rounded-tr-md [&_table_tr:first-child]:bg-gray-300 [&_table_tr:first-child]:text-white [&_table_tr:first-child]:font-semibold [&_tbody_tr:hover]:bg-gray-100 [&_tbody_tr:nth-child(even)]:bg-gray-50 [&_table_tr:first-child_td]:px-4 [&_table_tr:first-child_td]:py-3 [&_td]:px-4 [&_td]:py-3 ![&_table]:text-center ![&_table_tr:first-child]:text-black"
              v-html="productStore.product.description"
            />
          </div>
          <div class="block md:basis-1/2">
            <NuxtImg
              v-if="productStore.product?.featuredAsset"
              :src="productStore.product.featuredAsset.preview"
              class="w-full rounded-md hidden md:block"
              alt="Main image preview"
            />
            <NSkeleton v-else shape="rect" class="aspect-1/1 w-full rounded-md" />
          </div>
        </section>
        <!-- mascot - why you should buy on our store -->
        <section>
          <div class="my-8 px4 lg:px0">
            <NSkeleton v-if="productLoading" class="h-150 w-full rounded-md" />
            <template v-else>
              <div class="w-full flex items-center justify-center gap-x-3 rounded-md bg-slate-100 py5">
                <NuxtImg src="/webcontent/single_mascot.webp" class="max-w-120px lg:max-w-250px" />
                <div class="items-left h-full flex flex-col gap-y-0.5">
                  <h2 class="text-md mb-2 text-slate-900 fw600 lg:text-xl">
                    {{ t('trustable.productpage.mascot_banner.why_you_should_buy_at_our_store') }}
                  </h2>
                  <p class="text-md lg:text-md text-slate-900 fw600">
                    {{ t('trustable.productpage.mascot_banner.reason_1', [$config.public.returnWindowDays]) }}
                  </p>
                  <p class="lg:text-md text-md text-slate-900 fw600">
                    {{ t('trustable.productpage.mascot_banner.reason_2', [$config.public.company.phoneNumber]) }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </section>
        <!-- guaranty of satisfaction -->
        <section v-if="!productLoading" class="my-8 px4 lg:px0">
          <CustomerGuaranteeWide />
        </section>

        <ContentGuard>
          <div v-if="!productLoading" class="px4 sm:mb8 lg:px8 sm:px6">
            <div class="w-full inline-flex items-center justify-center">
              <span class="relative h-3 w-3 flex">
                <span class="absolute h-full w-full inline-flex animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span class="relative h-3 w-3 inline-flex rounded-full bg-emerald-500" />
              </span>
              <div class="ml3 text-context fw600 uppercase" n="base emerald8">
                {{ t('policies.satisfaction_guarantee.customer_support.we_are_here_for_you') }}
              </div>
            </div>

            <div class="text-center">
              <NButton
                id="contact-support"
                icon="i-heroicons:envelope"
                :href="`mailto:${$config.public.company.emailAddress}`"
                class="mt2 w-full justify-center border-0 drop-shadow-none sm:w-auto"
              >
                {{ $config.public.company.emailAddress }}
              </NButton>
            </div>
          </div>
          <div v-if="!productLoading" class="px4 sm:mb8 lg:px8 sm:px6">
            <div class="relative mx-auto mt-12 max-w-md w-fit flex items-center rounded-lg bg-blue-950 py4 pl-[90px] pr4">
              <NuxtImg class="absolute mr-4 h-25 w-25 border-4 border-blue-950 rounded-full bg-gray-50 -left-5" src="/webcontent/andrea-support.webp" alt="Andrea" />
              <div class="text-white">
                <p class="text-md text-center font-bold">
                  Máte dotaz a potřebujete poradit?
                </p>
                <p class="text-center text-xl font-bold">
                  {{ $config.public.company.phoneNumber }}
                </p>
              </div>
            </div>
          </div>
        </ContentGuard>
      </main>
      <div
        v-if="!productLoading"
        class="mx-auto max-w-3xl px2 pb8 pt2 lg:max-w-[88rem] lg:px6 md:max-w-4xl sm:px4 sm:pb12 sm:pt6"
      >
        <section v-if="productStore.product" class="my5 mt5">
          <ShopReviews :product-data="productStore.product" />
        </section>
      </div>

      <div class="mx-auto max-w-3xl px2 pb8 pt2 lg:max-w-[88rem] lg:px6 md:max-w-4xl sm:px4 sm:pb12 sm:pt6">
        <ClientOnly>
          <section v-if="historyItems?.length" aria-labelledby="viewed-products-title" class="mb12">
            <div>
              <h2 id="viewed-products-title" class="text-base fw600" v-text="t('product.recently_viewed.title')" />
            </div>

            <div class="mb6">
              <div class="grid grid-cols-2 mt6 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem" data-testid="productGrid">
                <ProductCard
                  v-for="item in historyItems" :key="item.id" :item="item"
                  :data-testid="`productGridItem-${item.id}`" @click="handleClick(item)"
                />
              </div>
            </div>
          </section>

          <section v-if="productStore.productCollectionId" aria-labelledby="related-products-title">
            <div>
              <h2 id="related-products-title" class="text-base fw600" v-text="t('product.related_items.title')" />
            </div>
            <LazyProductsGrid
              :blocking="false"
              :search="{ collectionId: productStore.productCollectionId }"
              @total="(v) => (total = v)"
              @loading="(loading) => (isLoading = loading)"
            />
          </section>
        </ClientOnly>
      </div>

      <ClientOnly>
        <NBottomSheet
          v-if="productStore.product?.id"
          :show="showBottomSheet"
          closable
          @close="showBottomSheet = false"
        >
          <template #header>
            <div
              class="w-full flex items-center justify-between px4 py3"
              :class="{ 'border-b n-border-base': !arrivedState.top }"
            >
              <div class="flex items-center">
                <NuxtImg
                  :src="productStore.selectedVariant?.featuredAsset?.preview ?? productStore.product?.featuredAsset?.preview" :alt="productStore.product?.name"
                  class="mr2 h16 w16"
                  width="100"
                  height="100"
                  loading="lazy"
                />
                <div>
                  <div class="line-clamp-1 overflow-hidden text-context" n="sm slate5 dak:slate4">
                    {{ productStore.product.name }}
                  </div>

                  <div
                    v-if="productStore.selectedVariant?.currency && productStore.selectedVariant?.price"
                    class="flex items-center justify-start space-x-2"
                  >
                    <span class="text-context font-bold" n="xl slate9 dark:slate4">
                      {{ priceFormatter(productStore.price, productStore.selectedVariant.currency) }}
                    </span>
                    <template v-if="productStore?.priceBeforeDiscount && productStore.priceBeforeDiscount > productStore.selectedVariant.price">
                      <span class="text-context line-through" n="slate5 lg">
                        {{ priceFormatter(productStore.priceBeforeDiscount, productStore.selectedVariant.currency) }}
                      </span>
                      <NBadge v-if="productStore.discountPercentage" n="xs orange5" class="fw600 tracking-wider !rd-full">
                        -{{ productStore.discountPercentage }}%
                      </NBadge>
                    </template>
                  </div>
                </div>
              </div>

              <div class="h7 flex items-center -mt8">
                <NButton
                  id="close-bottom-sheet"
                  n="lg borderless"
                  icon="i-heroicons:x-mark"
                  class="p-0.25em children:m-0 sm:-m-2"
                  @click="showBottomSheet = false"
                />
              </div>
            </div>
          </template>

          <div ref="bottomSheetScrollContainer" class="flex-1 overflow-y-auto">
            <div class="mb8 px4 pb2">
              <ProductOptionSelector
                v-model:show-option-errors="showOptionErrors"
                v-model:size-chart-modal-open="sizeChartModalOpen"
                :has-size-chart
              />
            </div>
          </div>

          <template #footer>
            <div class="pb-safe">
              <div class="p4 pb6">
                <AddToCart
                  class="pt5"
                  :loading="addingToCart"
                  @add-to-cart="handleAddToCart(productStore.activeVariant)"
                  @change:order-line="handleAdjustOrderLine"
                />
              </div>
            </div>
          </template>
        </NBottomSheet>

        <NModal :open="vatModalOpen" @close="vatModalOpen = false">
          <HDialogPanel
            class="max-w-md w-full transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
          >
            <div class="absolute right-0 top-0 pr4 pt4">
              <NButton
                id="close-vat-info"
                n="lg borderless"
                icon="i-heroicons:x-mark"
                class="p-0.25em text-context children:m-0"
                @click="vatModalOpen = false"
              />
            </div>

            <div>
              <div class="mx-auto h12 w12 flex items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900">
                <Icon name="i-heroicons:receipt-percent-solid" class="text-2xl text-orange5" />
              </div>

              <div class="mt3 text-center sm:mt5">
                <h3 id="modal-title" class="text-base c-slate9 font-semibold leading-6">
                  {{ t('product.vat_included.title') }}
                </h3>

                <div class="mt2">
                  <p n="sm slate5 dark:slate7" class="text-context">
                    {{ t('product.vat_included.body') }}
                  </p>
                </div>
              </div>
            </div>
          </HDialogPanel>
        </NModal>
      </ClientOnly>
    </div>
  </div>
</template>
